import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MdClose, MdAccessTime } from 'react-icons/md';
import Card from '../../components/common/Card';
import { getStudentMonthlyAttendance } from '../../services/attendanceService';
import { getMyHistory } from '../../services/sessionService';
import api from '../../services/api';
import { useCalendar } from '../../context/CalendarContext';
import { ktmDayKey, ktmThisMonth } from '../../utils/calendar';

const THIS_MONTH = ktmThisMonth();

const fmtTime = (t) => {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
};

const fmtDateNice = (str) => {
  if (!str) return '';
  const d = new Date(str + 'T00:00:00');
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
};

// Circular progress ring
function ProgressRing({ percentage, size = 90 }) {
  const r    = size / 2 - 8;
  const circ = 2 * Math.PI * r;
  const dash = (percentage / 100) * circ;
  const color = percentage >= 75 ? '#22C55E' : percentage >= 50 ? '#F97316' : '#EF4444';
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg viewBox={`0 0 ${size} ${size}`} className="w-full h-full -rotate-90">
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#F3F4F6" strokeWidth="8" />
        <circle
          cx={size/2} cy={size/2} r={r} fill="none"
          stroke={color} strokeWidth="8"
          strokeDasharray={`${dash} ${circ - dash}`}
          strokeLinecap="round"
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-base font-bold text-secondary">{percentage}%</span>
      </div>
    </div>
  );
}

// Student day detail panel
function DayDetailPanel({ dateStr, records, onClose }) {
  const { formatDateNice } = useCalendar();
  const dayRecords = records.filter(r => {
    const date = r.sessionId?.date;
    if (!date) return false;
    return ktmDayKey(date) === dateStr; // match by Kathmandu civil day
  });

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/20" onClick={onClose} />
      <div className="fixed inset-y-0 right-0 z-50 w-full max-w-sm bg-white shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
          <div>
            <h2 className="font-bold text-gray-900">{formatDateNice(dateStr + 'T00:00:00')}</h2>
            <p className="text-xs text-gray-400 mt-0.5">
              {dayRecords.length} session{dayRecords.length !== 1 ? 's' : ''}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <MdClose size={20} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {dayRecords.length === 0 && (
            <p className="text-center py-8 text-gray-400 text-sm">No sessions found for this day</p>
          )}
          {dayRecords.map(r => (
            <div
              key={r._id}
              className={`bg-white rounded-2xl p-4 border
                ${r.status === 'absent' ? 'border-red-100'
                : r.status === 'late'   ? 'border-amber-500/25'
                : 'border-green-100'}`}
            >
              <div className="flex items-center justify-between gap-2">
                <p className="font-semibold text-gray-800 text-sm truncate">
                  {r.sessionId?.className || 'Session'}
                </p>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0
                  ${r.status === 'absent' ? 'bg-red-50 text-red-500'
                  : r.status === 'late'   ? 'bg-amber-500/15 text-amber-400'
                  : 'bg-green-50 text-green-600'}`}>
                  {r.status === 'absent' ? 'Absent' : r.status === 'late' ? 'Late' : 'Present'}
                </span>
              </div>
              {r.sessionId?.startTime && (
                <p className="text-xs text-gray-400 mt-1.5 flex items-center gap-0.5">
                  <MdAccessTime size={11} /> {fmtTime(r.sessionId.startTime)}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

// Calendar grid for a month
function AttendanceCalendar({ days, month, onDayClick }) {
  const [y, m]      = month.split('-').map(Number);
  const firstDay    = new Date(y, m - 1, 1).getDay();
  const daysInMonth = new Date(y, m, 0).getDate();

  const dayMap = {};
  days.forEach(d => {
    const key = ktmDayKey(d.date); // key by Kathmandu civil day
    dayMap[key] = d.status;
  });

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let i = 1; i <= daysInMonth; i++) {
    const dateStr = `${y}-${String(m).padStart(2,'0')}-${String(i).padStart(2,'0')}`;
    cells.push({ day: i, status: dayMap[dateStr] || null, dateStr });
  }

  return (
    <div>
      <div className="grid grid-cols-7 gap-1 mb-1">
        {['S','M','T','W','T','F','S'].map((d, i) => (
          <div key={i} className="text-center text-xs font-semibold text-gray-400">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((cell, i) => (
          <div
            key={i}
            onClick={() => cell?.status && onDayClick?.(cell.dateStr)}
            className={`aspect-square rounded-lg flex items-center justify-center text-xs font-medium transition-opacity
              ${cell?.status ? 'cursor-pointer hover:opacity-75' : ''}
              ${!cell ? '' :
              cell.status === 'present' ? 'bg-green-100 text-green-700' :
              cell.status === 'late'    ? 'bg-amber-500/15 text-amber-400' :
              cell.status === 'absent'  ? 'bg-red-100 text-red-600' :
              'bg-gray-50 text-gray-400'
            }`}
          >
            {cell?.day || ''}
          </div>
        ))}
      </div>
      <div className="flex items-center gap-4 mt-3 text-xs text-gray-500 flex-wrap">
        {[['bg-green-100', 'Present'], ['bg-amber-500/15', 'Late'], ['bg-red-100', 'Absent'], ['bg-gray-50', 'No class']].map(([cls, label]) => (
          <span key={label} className="flex items-center gap-1">
            <span className={`w-3 h-3 rounded ${cls}`} />
            {label}
          </span>
        ))}
      </div>
      {days.some(d => d.status) && (
        <p className="text-xs text-gray-400 mt-2">Tap a coloured day to see session details</p>
      )}
    </div>
  );
}

function MyAttendance() {
  document.title = 'Attendance — TrainAxis';
  const [month,       setMonth]       = useState(THIS_MONTH);
  const [selectedDay, setSelectedDay] = useState(null);

  const { data: profileData } = useQuery({
    queryKey: ['student-me'],
    queryFn:  () => api.get('/api/students/me').then(r => r.data),
  });
  const profile = profileData?.profile;

  const { data, isPending } = useQuery({
    queryKey: ['my-att-monthly', profile?._id, month],
    queryFn:  () => getStudentMonthlyAttendance(profile._id, month).then(r => r.data),
    enabled:  !!profile?._id,
  });

  const { data: historyData } = useQuery({
    queryKey: ['my-history'],
    queryFn:  getMyHistory,
    staleTime: 60000,
  });
  const historyRecords = historyData?.records || [];

  const days       = data?.days       || [];
  const percentage = data?.percentage ?? 0;
  const present    = data?.present    ?? 0;
  const absent     = data?.absent     ?? 0;
  const late       = data?.late       ?? 0;
  const total      = data?.total      ?? 0;

  const standing = percentage >= 75 ? '✅ Good standing' : percentage >= 50 ? '⚠️ Needs improvement' : '❌ At risk';

  return (
    <div className="space-y-4">
      {/* Month selector */}
      <div className="flex items-center gap-3 flex-wrap">
        <input
          type="month"
          value={month}
          max={THIS_MONTH}
          onChange={e => setMonth(e.target.value)}
          className="border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
        />
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Total Sessions', value: total,   color: 'text-secondary'  },
          { label: 'Present',        value: present, color: 'text-green-600'  },
          { label: 'Late',           value: late,    color: 'text-amber-500' },
          { label: 'Absent',         value: absent,  color: 'text-red-600'    },
        ].map(({ label, value, color }) => (
          <Card key={label} className="text-center">
            <p className="text-xs text-gray-400">{label}</p>
            <p className={`text-2xl font-bold mt-1 ${color}`}>{isPending ? '…' : value}</p>
          </Card>
        ))}
      </div>

      {/* Calendar + ring */}
      <Card>
        <div className="flex flex-col sm:flex-row gap-6 items-start">
          <div className="flex-1">
            {isPending ? (
              <div className="h-40 bg-gray-100 animate-pulse rounded-xl" />
            ) : (
              <AttendanceCalendar
                days={days}
                month={month}
                onDayClick={(dateStr) => setSelectedDay(dateStr)}
              />
            )}
          </div>
          <div className="flex flex-col items-center gap-2 sm:pt-4">
            <ProgressRing percentage={percentage} size={100} />
            <p className="text-sm font-semibold text-secondary">Attendance Rate</p>
            <p className="text-xs text-gray-400">{standing}</p>
          </div>
        </div>
      </Card>

      {/* Day detail panel */}
      {selectedDay && (
        <DayDetailPanel
          dateStr={selectedDay}
          records={historyRecords}
          onClose={() => setSelectedDay(null)}
        />
      )}
    </div>
  );
}

export default MyAttendance;
